import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Dialog } from "../Dialog";
import {
  KINDS,
  MODES,
  buildClarificationAnswers,
  buildGenerateRequest,
  normalizeTemplates,
  readClarification,
  validateGenerateForm,
} from "../../utils/workflowGenerationRequest";
import { generateWorkflow, getWorkflowTemplates } from "../../services/workflowGeneration";
import { useModelOptions } from "../../hooks/useModelOptions";
import { describeModelOption } from "../../utils/modelRegistry";

const fieldStyles = `
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const Hint = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #a0a6b1;
`;

const Input = styled.input`
  ${fieldStyles}
`;

const Textarea = styled.textarea`
  ${fieldStyles}
  resize: vertical;
  line-height: 1.5;
`;

const Select = styled.select`
  ${fieldStyles}
  cursor: pointer;

  /* Pure white reads better than the 80% white font colour on the dark theme's dark field.
     Scoped to the dark theme so the light theme keeps dark text on its white field. */
  [data-aw-theme="dark"] & {
    color: #ffffff;
  }

  /* Pin the open list: some browsers draw the popup light while inheriting the control's
     colour, which would make the options unreadable. */
  & option {
    color: var(--main-font-color);
    background-color: var(--main-bg);
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--main-font-color);
  cursor: pointer;
`;

const ErrorBanner = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #c0392b;
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
`;

const Linkish = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--button-bg);
  cursor: pointer;
  width: fit-content;
`;

const NO_TEMPLATE = "none";

const ClarificationBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #d68910;
  border-left: 4px solid #d68910;
  border-radius: 8px;
  background-color: rgba(214, 137, 16, 0.08);
`;

const ClarificationTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--main-font-color);
`;

const ClarificationMessage = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: var(--main-font-color);
  opacity: 0.85;
`;

/**
 * Describe a workflow in natural language and have the generator build it.
 *
 * Sends POST /admin/workflow/generate with `save: true`, so a workflow record is created and
 * handed back to the caller via `onGenerated`.
 */
export const GenerateWorkflowDialog = ({
  show,
  onClose = () => { },
  onGenerated = () => { },
  defaultName = "",
  defaultDescription = "",
  defaultPurpose = "",
  defaultKind = "agent",
  clientId = null,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [purpose, setPurpose] = useState("");
  const [kind, setKind] = useState("agent");
  const [mode, setMode] = useState("auto");
  const [template, setTemplate] = useState(NO_TEMPLATE);
  const [verify, setVerify] = useState(false);
  const [verifyCycles, setVerifyCycles] = useState(2);
  const [allowPython, setAllowPython] = useState(false);
  const [maxTokens, setMaxTokens] = useState("");
  const [maxRepairs, setMaxRepairs] = useState("");
  const [maxExpansions, setMaxExpansions] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [model, setModel] = useState("");
  const [useSecondaryEndpoint, setUseSecondaryEndpoint] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState(null);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [clarification, setClarification] = useState(null);
  const [answers, setAnswers] = useState({});
  const [clarificationRound, setClarificationRound] = useState(0);

  useEffect(() => {
    if (!show) {
      return;
    }

    setName(defaultName ?? "");
    setDescription(defaultDescription ?? "");
    setPurpose(defaultPurpose ?? "");
    setKind(defaultKind || "agent");
    setMode("auto");
    setTemplate(NO_TEMPLATE);
    setVerify(false);
    setVerifyCycles(2);
    setAllowPython(false);
    setMaxTokens("");
    setMaxRepairs("");
    setMaxExpansions("");
    setShowAdvanced(false);
    setModel("");
    setUseSecondaryEndpoint(false);
    setError(null);
    setGenerating(false);
    setClarification(null);
    setAnswers({});
    setClarificationRound(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (!show) {
      return;
    }

    let cancelled = false;

    getWorkflowTemplates()
      .then((results) => {
        if (cancelled) return;
        if (results?.success === false) {
          setTemplatesError(results.message || "Could not load templates.");
          setTemplates([]);
          return;
        }
        setTemplatesError(null);
        setTemplates(normalizeTemplates(results?.templates));
      })
      .catch((templatesFailure) => {
        if (!cancelled) {
          setTemplatesError(templatesFailure?.message || "Could not load templates.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [show]);

  // Registered model endpoints, scoped to the client when one is known.
  const { models: availableModels, options: modelOptions, loading: modelsLoading, error: modelsError } = useModelOptions({ clientId });

  const form = {
    name,
    description,
    purpose,
    kind,
    mode,
    template,
    verify,
    verifyCycles,
    allowPython,
    maxTokens,
    maxRepairs,
    maxExpansions,
    model,
    useSecondaryEndpoint,
  };

  const submit = async () => {
    if (generating) {
      return;
    }

    const validation = validateGenerateForm(form);
    if (!validation.ok) {
      setError(validation.errors[0]);
      return;
    }

    // Answering a clarification round: send the answers alongside the same description/options,
    // because generation is stateless.
    const clarificationAnswers = clarification
      ? buildClarificationAnswers(clarification.questions, answers)
      : null;

    if (clarification && Object.keys(clarificationAnswers).length === 0) {
      setError("Answer at least one of the questions so the generator can continue.");
      return;
    }

    setError(null);
    setGenerating(true);

    try {
      const results = await generateWorkflow(
        buildGenerateRequest({ ...form, clientId, clarificationAnswers })
      );

      // The generator may ask for more information instead of returning a graph; present the
      // questions and wait for answers rather than treating it as a failure.
      const nextClarification = readClarification(results);

      if (nextClarification) {
        setClarification(nextClarification);
        setClarificationRound((round) => round + 1);
        setAnswers((current) => {
          const kept = {};
          nextClarification.questions.forEach((question) => {
            if (current[question]) kept[question] = current[question];
          });
          return kept;
        });
        setError(null);
        return;
      }

      if (!results?.success) {
        setError(results?.message || "The generator could not build a workflow.");
        return;
      }

      setClarification(null);
      setAnswers({});
      onGenerated(results.saved ?? null, results);
    } catch (generationError) {
      setError(generationError?.message || "The generator could not build a workflow.");
    } finally {
      setGenerating(false);
    }
  };

  const selectedMode = MODES.find((option) => option.value === mode) ?? MODES[0];
  const selectedKind = KINDS.find((option) => option.value === kind) ?? KINDS[0];
  const templateOptions = [
    { value: NO_TEMPLATE, label: templates.length > 0 ? "No template — let it choose" : "No template" },
    ...templates,
  ];

  return (
    <Dialog
      title="Generate a workflow"
      subtitle={clarification ? "Answer the questions to continue" : "Describe what the workflow should do"}
      show={show}
      onClose={onClose}
      onOK={submit}
      okText={generating ? "Generating..." : clarification ? "Continue" : "Generate"}
      closeText="Cancel"
      style={{ width: "90%", maxWidth: "820px", height: "85vh" }}
    >
      {clarification ? (
        <ClarificationBox>
          <ClarificationTitle>
            <span>More information needed</span>
            {clarificationRound > 1 ? <Hint>round {clarificationRound}</Hint> : null}
          </ClarificationTitle>

          {clarification.message && clarification.questions[0] !== clarification.message ? (
            <ClarificationMessage>{clarification.message}</ClarificationMessage>
          ) : null}

          {clarification.questions.map((question) => (
            <Field key={question}>
              <Label>{question}</Label>
              <Textarea
                rows={2}
                value={answers[question] ?? ""}
                onChange={(event) =>
                  setAnswers((current) => ({ ...current, [question]: event.target.value }))
                }
                placeholder="Your answer"
              />
            </Field>
          ))}
        </ClarificationBox>
      ) : null}

      <Field>
        <Label>Description</Label>
        <Textarea
          rows={5}
          value={description}
          placeholder="e.g. Answer customer questions using the Support Docs dataset, cite the source, and hand off to a human when the answer is not in the data."
          onChange={(event) => setDescription(event.target.value)}
        />
        <Hint>Plain language is fine — this is what the planner works from.</Hint>
      </Field>

      <Row>
        <Field>
          <Label>Purpose (optional)</Label>
          <Input value={purpose} onChange={(event) => setPurpose(event.target.value)} placeholder="One line on its goal" />
        </Field>
      </Row>

      <Row>
        <Field style={{ flex: "1 1 220px" }}>
          <Label>Kind</Label>
          <Select value={kind} onChange={(event) => setKind(event.target.value)}>
            {KINDS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
          <Hint>{selectedKind.help}</Hint>
        </Field>

        <Field style={{ flex: "1 1 220px" }}>
          <Label>Mode</Label>
          <Select value={mode} onChange={(event) => setMode(event.target.value)}>
            {MODES.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
          <Hint>{selectedMode.help}</Hint>
        </Field>
      </Row>

      <Field>
        <Label>Template</Label>
        <Select value={template} onChange={(event) => setTemplate(event.target.value)}>
          {templateOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
        <Hint>
          {templatesError
            ? `Templates unavailable (${templatesError}). Generation still works without one.`
            : "Choose a known shape for deterministic generation, or leave it to the planner."}
        </Hint>
      </Field>

      <Field>
        <Label>Model</Label>
        <Select value={model} onChange={(event) => setModel(event.target.value)}>
          <option value="">
            {modelsLoading ? "Loading models…" : "Environment default (no model override)"}
          </option>
          {modelOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </Select>
        <Hint>
          {modelsError
            ? `Model registry unavailable (${modelsError}). Generation still falls back to the environment deployment.`
            : model
              ? describeModelOption(availableModels, model)
              : "Registered model endpoints. Leave as default to use the environment deployment."}
        </Hint>
      </Field>

      <Field>
        <Label>Max output tokens</Label>
        <Input
          type="number"
          min={1}
          step={1000}
          placeholder="32000"
          value={maxTokens}
          onChange={(event) => setMaxTokens(event.target.value)}
        />
        <Hint>Cap on the model's response for generation. Leave empty to use the server default.</Hint>
      </Field>

      <Linkish onClick={() => setShowAdvanced(!showAdvanced)}>
        {showAdvanced ? "Hide advanced options" : "Show advanced options"}
      </Linkish>

      {showAdvanced && (
        <>
          <Row>
            <CheckRow style={{ flex: "1 1 200px" }}>
              <input type="checkbox" checked={verify} onChange={(event) => setVerify(event.target.checked)} />
              Verify the graph after generating
            </CheckRow>
            {verify && (
              <Field style={{ flex: "1 1 140px" }}>
                <Label>Verify cycles</Label>
                <Input
                  type="number"
                  min={0}
                  value={verifyCycles}
                  onChange={(event) => setVerifyCycles(event.target.value)}
                />
              </Field>
            )}
          </Row>

          <Row>
            <CheckRow style={{ flex: "1 1 240px" }}>
              <input
                type="checkbox"
                checked={allowPython}
                onChange={(event) => setAllowPython(event.target.checked)}
              />
              Allow Python nodes
            </CheckRow>
            <Field style={{ flex: "1 1 140px" }}>
              <Label>Max repairs</Label>
              <Input
                type="number"
                min={0}
                value={maxRepairs}
                placeholder="default"
                onChange={(event) => setMaxRepairs(event.target.value)}
              />
            </Field>
            <Field style={{ flex: "1 1 140px" }}>
              <Label>Max expansions</Label>
              <Input
                type="number"
                min={0}
                value={maxExpansions}
                placeholder="default"
                onChange={(event) => setMaxExpansions(event.target.value)}
              />
            </Field>
          </Row>
          <Row>
            <CheckRow>
              <input
                type="checkbox"
                checked={useSecondaryEndpoint}
                onChange={(event) => setUseSecondaryEndpoint(event.target.checked)}
              />
              Use the secondary endpoint fallback
            </CheckRow>
          </Row>
        </>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}
    </Dialog>
  );
};

export default GenerateWorkflowDialog;
